import { Fragment, useState } from 'react';
import _ from 'lodash';
import { isValid, format } from 'date-fns';
//MUI
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
//constants
import { columnType, DataType, initialTableState, tableProps, tableState, Order } from './types';
import { OrderBy } from 'constants/global.constants';
//components
import { EnhancedTableHead, stableSort, getComparator, handleRequestSort } from './sorts';
import { TableLink } from 'theme/elements/Text';
import { Box, Typography } from '@mui/material';
import { CustomTable } from 'theme/elements/Table';
import { Stack } from '@mui/system';
import FilterNavbar, { getFilteredData } from './filter';
import { timing } from 'constants/ui.constants';
import { useAppSelector } from 'hooks/redux.hooks';
import { getMobile } from 'state/reducers/ui.reducers';

export const formatValue = (
  type: DataType,
  value: any,
  col: columnType,
  reccord: { [key: string]: string | number }
) => {
  switch (type) {
    case 'date':
      return isValid(new Date(value)) && !_.isNull(value)
        ? format(new Date(value), col?.formatDate || timing.DATE)
        : value;
    case 'link':
      return <TableLink title={value} />;
    case 'custom':
      if (col.customValueHandle) {
        return col.customValueHandle(value, reccord);
      }
      return value;
    default:
      return value || '';
  }
};
const getPagesAmounts = (total: number, perPage: number) => {
  let res = total - (total % perPage);
  let pages = res / perPage;
  return total % perPage > 0 ? pages + 1 : pages;
};

export default function BasicTable(props: tableProps) {
  let {
    data,
    columns,
    rowsClass,
    filterList,
    includeSearchBox,
    searchColumns,
    noPagination,
    noSlice,
    hideHeader,
    tableClass,
    defaultSortBy,
    defaultOrderBy,
    perPage,
    navbarSearchMode,
    navbarSearchWord,
    handleChangeOnClose,
    headerComponent,
    triggerFilteredData,
  } = props;
  const [tableState, setTableState] = useState<tableState>({
    ...initialTableState,
    sortBy: defaultSortBy || '',
    orderBy: defaultOrderBy || (OrderBy.DESENDING as Order),
    perPage: perPage || 10,
  });
  const isMobile = useAppSelector(getMobile);
  const filteredData = getFilteredData(data, tableState.filters, searchColumns);
  triggerFilteredData && triggerFilteredData(filteredData)
  const sliced = () => {
    if (noSlice) {
      return stableSort(filteredData, getComparator(tableState.orderBy, tableState.sortBy || ''))
    }
    return stableSort(filteredData, getComparator(tableState.orderBy, tableState.sortBy || '')).slice(
      tableState.page * tableState.perPage,
      tableState.page * tableState.perPage + tableState.perPage
    );
  };
  const showActions = () => {
    if (props.noShrink) {
      return false;
    }
    if (isMobile) {
      return props?.actionsCell ? true : false;
    }

    return true;
  };

  const actionMatchesRow = (action: NonNullable<tableProps['actionsCell']>[number], row: (typeof filteredData)[number]) => {
    if (action.match) {
      return action.match(row);
    }
    if (action.condition) {
      let values: any[] = [];
      if (!_.isArray(action.condition.value)) {
        values.push(action.condition.value);
      } else {
        values.push(...action.condition.value);
      }
      return (
        (values.includes(_.get(row, action.condition.column)) && row?.isPDF) ||
        (action.condition.column === 'status' && values.includes((row as any).statusValue))
      );
    }
    return true;
  };

  const renderActionsCells = (row: (typeof filteredData)[number], rowIndex: number) => {
    let actionsAmount = 0;
    const cells =
      props.actionsCell?.map((action, index) => {
        if (!actionMatchesRow(action, row)) {
          return <Fragment key={index}></Fragment>;
        }
        actionsAmount++;
        if (action.render) {
          return (
            <Box className="tableActionsWrap" key={index}>
              {action.render(row)}
            </Box>
          );
        }
        return (
          <Box
            className="tableActionsWrap"
            key={index}
            onClick={() => {
              action.onClick?.(row);
            }}
          >
            {action.element}
          </Box>
        );
      }) ?? [];
    const actionsColumn = (
      <TableCell
        className="shrinkCell"
        key={`actions-${rowIndex}`}
        sx={{ textAlign: 'end', verticalAlign: 'middle' }}
      >
        <Stack
          direction={'row'}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}
          spacing={1}
        >
          {cells}
        </Stack>
        {actionsAmount === 0 && <span className="empty"></span>}
      </TableCell>
    );
    return actionsColumn;
  };

  return (
    <div className='table-wrap-stack'>
      {filterList && (
        <FilterNavbar
          navbarSearchMode={navbarSearchMode}
          navbarSearchWord={navbarSearchWord}
          handleChangeOnClose={handleChangeOnClose}
          filterList={filterList}
          data={data}
          handleChange={setTableState}
          tableState={tableState}
          includeSearchBox={includeSearchBox}
          searchColumns={searchColumns}
          HeaderComponent={headerComponent}
        />
      )}
      <CustomTable spacing={3}>
        <TableContainer component={Paper}>
          <Table aria-label="simple table" className={tableClass || 'simpleTable'}>
            {!hideHeader && (
              <EnhancedTableHead
                headCells={columns}
                tableState={tableState}
                setState={setTableState}
                order={tableState.orderBy}
                orderBy={tableState.sortBy || ''}
                onRequestSort={handleRequestSort}
                rowCount={filteredData.length}
              />
            )}
            <TableBody>
              {sliced().map((row, i) => {
                return (
                  <TableRow
                    key={i}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    className={rowsClass}
                  >
                    {columns.map((col, inx) => (
                      <TableCell
                        {...col.eventsHandle}
                        id={String(_.get(row, col.fieldId))}
                        key={inx + i + 'z'}
                        component="th"
                        scope="row"
                      >
                        {!hideHeader ? (
                          <Stack direction={'row'}>
                            {!col.hideHeaderLabel && (
                              <Typography
                                component="span"
                                variant="boldBody1"
                                className="tabletext mobileHeader"
                              >
                                {col.title}
                              </Typography>
                            )}
                            <Typography
                              component="span"
                              variant="body1"
                              className="tabletext"
                            >
                              {formatValue(col.type, _.get(row, col.name), col, row)}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography component="span" variant="body1" className="tabletext">
                            {formatValue(col.type, _.get(row, col.name), col, row)}
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                    {showActions() && renderActionsCells(row, i)}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredData.length > tableState.perPage && !noPagination && (
          <Stack
            direction="row"
            padding={{ xs: '0 24px', sm: 0 }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography width={{ sm: 'auto', lg: '200px' }} variant="captionRegular">
              Showing {tableState.page * tableState.perPage + 1} -{' '}
              {tableState.page * tableState.perPage + sliced().length} from {filteredData.length}
            </Typography>
            <Pagination
              shape="rounded"
              count={getPagesAmounts(filteredData.length, tableState.perPage)}
              page={tableState.page + 1}
              onChange={(event: unknown, newPage: number) => {
                setTableState({ ...tableState, page: newPage - 1 });
              }}
            />
            <Typography className="hideOnMobile" width="200px">
              &nbsp;
            </Typography>
          </Stack>
        )}
      </CustomTable>
    </div>
  );
}
